import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import {
    listPlugins,
    getProjectPlugins,
    setProjectPlugins,
} from "../lib/api";

/**
 * Inline plugin picker rendered at the top of the Estimate / Analyze
 * dialogs. Plain horizontal strip of plugin icons — tap to toggle,
 * persistence is per-project via `PUT /projects/:id/plugins`.
 *
 * iter-176: stripped presets, active counter, and helper copy per
 * user feedback — the picker is icons-only now.
 */
export default function InlinePluginPicker({
    projectId,
    onChange,
    testIdPrefix = "inline-plugin-picker",
}) {
    const [plugins, setPlugins] = useState([]);
    const [activeIds, setActiveIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cat = await listPlugins();
                if (cancelled) return;
                setPlugins(cat || []);
                if (projectId) {
                    const ids = await getProjectPlugins(projectId);
                    if (cancelled) return;
                    setActiveIds(new Set(ids));
                    onChange && onChange(ids);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("inline plugin picker load failed", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const toggle = async (p) => {
        if (p.status !== "active") {
            toast.info(`${p.name} is disabled.`);
            return;
        }
        const prev = activeIds;
        const next = new Set(prev);
        if (next.has(p.id)) next.delete(p.id);
        else next.add(p.id);
        setActiveIds(next);
        onChange && onChange(Array.from(next));
        if (!projectId) return;
        setSaving(true);
        try {
            const saved = await setProjectPlugins(projectId, Array.from(next));
            const savedSet = new Set(saved);
            setActiveIds(savedSet);
            onChange && onChange(saved);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save plugin");
            setActiveIds(prev);
            onChange && onChange(Array.from(prev));
        } finally {
            setSaving(false);
        }
    };

    if (loading || !plugins.length) return null;

    return (
        <section
            className="rounded-sm border border-white/15 bg-[#0a0a0a] p-2"
            data-testid={testIdPrefix}
        >
            <div
                className="flex items-center gap-2 overflow-x-auto py-0.5 [scrollbar-width:thin]"
                data-testid={`${testIdPrefix}-items`}
            >
                {plugins.map((p) => {
                    const active = activeIds.has(p.id);
                    const disabled = p.status !== "active";
                    return (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => toggle(p)}
                            disabled={saving}
                            data-testid={`${testIdPrefix}-${p.id}`}
                            data-active={active ? "true" : "false"}
                            title={`${p.name} — ${
                                disabled
                                    ? "Disabled"
                                    : active
                                        ? "Click to disable"
                                        : "Click to enable"
                            }`}
                            className={`relative w-11 h-11 sm:w-12 sm:h-12 rounded-md overflow-hidden transition-all active:scale-95 shrink-0 ${
                                active
                                    ? "ring-2 ring-emerald-400 shadow-[inset_0_2px_8px_rgba(0,0,0,0.45)] translate-y-[1px] brightness-110"
                                    : "shadow-[0_2px_6px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.08)] hover:shadow-[0_4px_10px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.12)] brightness-90 hover:brightness-100"
                            } ${disabled ? "opacity-60 grayscale" : ""}`}
                        >
                            {p.icon_type === "video" ? (
                                <video
                                    src={p.icon}
                                    poster={p.poster || undefined}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="w-full h-full object-cover"
                                />
                            ) : p.icon ? (
                                <img
                                    src={p.icon}
                                    alt={p.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div
                                    className="w-full h-full flex items-center justify-center font-black text-lg tracking-tight"
                                    style={{
                                        color: "#0a1128",
                                        backgroundColor: p.accent || "#334155",
                                    }}
                                >
                                    {p.name[0]}
                                </div>
                            )}
                            {active && (
                                <div
                                    aria-hidden
                                    className="absolute inset-0 ring-inset ring-2 ring-emerald-400/80 bg-emerald-400/10 rounded-md"
                                />
                            )}
                            {disabled && (
                                <div className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-slate-900/80 flex items-center justify-center">
                                    <Lock className="w-2 h-2 text-slate-400" />
                                </div>
                            )}
                            <div
                                className={`absolute bottom-0 inset-x-0 py-0.5 text-center text-[8px] font-bold uppercase tracking-wider truncate ${
                                    active
                                        ? "bg-emerald-500/80 text-black"
                                        : "bg-white/[0.05] text-white"
                                }`}
                            >
                                {p.name}
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
